import React, { useState, useMemo } from 'react';
import { Card, Button, Form, InputGroup, Nav } from 'react-bootstrap';
import { FiSearch, FiPlus } from 'react-icons/fi';
import { useLanguage } from '../../../../../API/Context/LanguageContext';
import { useUser } from '../../../../../API/Context/UserContext';
import { useActivePatient } from '../../../../../API/Hooks/ActivePatientContext';
import type { FamilyMemberResponse } from '../../../../../Types/schema-helpers';
import { FamilyMemberCard } from './subwidgets/FamilyMemberCard';
import { AddFamilyMemberModal } from './subwidgets/AddFamilyMemberModal';

export const HereditaryWidget: React.FC = () => {
  const { lan } = useLanguage();
  const currentLanKey = (lan as number) === 0 ? 0 : 1;
  const { isProfessional } = useUser();
  const { activePatient } = useActivePatient();

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'maternal' | 'paternal' | 'siblings'>('all');
  const [showAddModal, setShowAddModal] = useState(false);

  // Extract hereditary payload from context
  const hereditary = activePatient?.health_background?.hereditary;

  const maternalMembers = useMemo(() => hereditary?.maternal ?? [], [hereditary]);
  const paternalMembers = useMemo(() => hereditary?.paternal ?? [], [hereditary]);
  const siblingMembers = useMemo(() => hereditary?.siblings ?? [], [hereditary]);

  const allFamilyMembers: FamilyMemberResponse[] = useMemo(() => {
    return [...maternalMembers, ...paternalMembers, ...siblingMembers];
  }, [maternalMembers, paternalMembers, siblingMembers]);

  // Tabbed filtering
  const membersByTab = useMemo(() => {
    switch (activeTab) {
      case 'maternal':
        return maternalMembers;
      case 'paternal':
        return paternalMembers;
      case 'siblings':
        return siblingMembers;
      default:
        return allFamilyMembers;
    }
  }, [activeTab, maternalMembers, paternalMembers, siblingMembers, allFamilyMembers]);

  // Query filtering
  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return membersByTab;
    const q = searchQuery.toLowerCase();

    return membersByTab.filter((member: FamilyMemberResponse) => {
      const nameMatch = member.display_name?.toLowerCase().includes(q);
      const conditionMatch = member.conditions?.some((c) => {
        const conditionName = c.condition_detail?.name || c.condition_id || '';
        return conditionName.toLowerCase().includes(q);
      });
      return nameMatch || conditionMatch;
    });
  }, [membersByTab, searchQuery]);

  return (
    <Card className="border-0 shadow-sm p-3">
      {/* 1. Header with Controls */}
      <div className="d-flex flex-wrap gap-2 mb-3 align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: '320px' }}>
          <InputGroup size="sm">
            <Form.Control
              placeholder={
                currentLanKey === 0
                  ? 'Buscar por familiar o afección...'
                  : 'Search relative or condition...'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <InputGroup.Text className="bg-white border-start-0 text-muted">
              <FiSearch size={15} />
            </InputGroup.Text>
          </InputGroup>
        </div>

        {isProfessional && (
          <Button
            variant="outline-primary"
            size="sm"
            className="d-flex align-items-center gap-1 rounded-pill px-3"
            onClick={() => setShowAddModal(true)}
          >
            <FiPlus size={16} />
            <span>{currentLanKey === 0 ? 'Agregar Familiar' : 'Add Family Member'}</span>
          </Button>
        )}
      </div>

      {/* 2. Lineage Tabs */}
      <Nav variant="tabs" className="mb-3 border-bottom-0" activeKey={activeTab}>
        <Nav.Item>
          <Nav.Link 
            eventKey="all" 
            onClick={() => setActiveTab('all')} 
            className="py-1 px-3 small fw-semibold"
          >
            {currentLanKey === 0 ? `Todos (${allFamilyMembers.length})` : `All (${allFamilyMembers.length})`}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            eventKey="maternal" 
            onClick={() => setActiveTab('maternal')} 
            className="py-1 px-3 small fw-semibold"
          >
            {currentLanKey === 0 ? `Materna (${maternalMembers.length})` : `Maternal (${maternalMembers.length})`}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            eventKey="paternal" 
            onClick={() => setActiveTab('paternal')} 
            className="py-1 px-3 small fw-semibold"
          >
            {currentLanKey === 0 ? `Paterna (${paternalMembers.length})` : `Paternal (${paternalMembers.length})`}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            eventKey="siblings" 
            onClick={() => setActiveTab('siblings')} 
            className="py-1 px-3 small fw-semibold"
          >
            {currentLanKey === 0 ? `Hermanos (${siblingMembers.length})` : `Siblings (${siblingMembers.length})`}
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {/* 3. Family Members Grid */}
      {filteredMembers.length === 0 ? (
        <p className="text-muted small fst-italic mb-0 py-4 text-center">
          {currentLanKey === 0
            ? 'No hay antecedentes heredofamiliares registrados para esta selección.'
            : 'No hereditary records found for this selection.'}
        </p>
      ) : (
        <div className="row g-3">
          {filteredMembers.map((member: FamilyMemberResponse) => (
            <div key={member.id} className="col-12 col-md-6 col-lg-4">
              <FamilyMemberCard member={member} isProfessional={isProfessional} />
            </div>
          ))}
        </div>
      )}

      {/* Add Family Member Modal */}
      <AddFamilyMemberModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
      />
    </Card>
  );
};